import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Plus, Star } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { inr } from "@/lib/api";
import { toast } from "sonner";

export const Stars = ({ value, size = 13 }) => (
  <span className="flex items-center gap-0.5" aria-label={`${value} star rating`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} fill={i <= Math.round(value) ? "var(--mn-gold)" : "none"} stroke="var(--mn-gold)" strokeWidth={1.5} />
    ))}
  </span>
);

export default function ProductCard({ product, onQuickView }) {
  const { addToCart } = useStore();
  const navigate = useNavigate();

  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      className="glass-card group relative flex flex-col overflow-hidden rounded-3xl transition-transform duration-500 hover:-translate-y-1.5"
      whileHover="hover"
    >
      <div
        className="relative aspect-[4/5] cursor-pointer overflow-hidden"
        onClick={() => navigate(`/product/${product.slug}`)}
        data-testid={`product-image-${product.slug}`}
      >
        <img
          src={product.images[0]}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-70" />
        {product.badge && (
          <span
            className="absolute left-3 top-3 rounded-full px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.2em]"
            style={{
              background: product.badge === "New" ? "var(--mn-mint)" : "var(--mn-gold)",
              color: "#070D09",
            }}
            data-testid={`badge-${product.slug}`}
          >
            {product.badge}
          </span>
        )}
        <div className="absolute inset-x-3 bottom-3 flex translate-y-3 gap-2 opacity-0 transition-all duration-400 group-hover:translate-y-0 group-hover:opacity-100">
          <button
            data-testid={`quick-view-${product.slug}`}
            onClick={(e) => { e.stopPropagation(); onQuickView?.(product); }}
            className="glass flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 text-xs font-medium text-white transition-colors hover:text-[var(--mn-gold-glow)]"
          >
            <Eye size={14} /> Quick View
          </button>
          <button
            data-testid={`quick-add-${product.slug}`}
            onClick={(e) => {
              e.stopPropagation();
              addToCart(product, product.variants[0].label);
              toast.success(`${product.name} added to your bag`);
            }}
            className="btn-gold flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 text-xs"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <p className="font-mono text-[0.58rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-gold)" }}>
          {product.type}
        </p>
        <h3
          className="mt-1.5 cursor-pointer font-serif text-base leading-snug transition-colors hover:text-[var(--mn-gold)] sm:text-lg"
          onClick={() => navigate(`/product/${product.slug}`)}
        >
          {product.name}
        </h3>
        <div className="mt-1.5 flex items-center gap-2">
          <Stars value={product.rating} />
          <span className="font-mono text-[0.65rem]" style={{ color: "var(--mn-text2)" }}>
            {product.rating} ({product.reviews_count.toLocaleString("en-IN")})
          </span>
        </div>
        <div className="mt-auto flex items-baseline gap-2 pt-3">
          <span className="font-mono text-base" style={{ color: "var(--mn-gold)" }}>{inr(product.price)}</span>
          {product.mrp > product.price && (
            <span className="font-mono text-xs line-through opacity-50">{inr(product.mrp)}</span>
          )}
        </div>
      </div>
    </motion.article>
  );
}
